<script setup lang="ts">
// TODO: This component needs to be refactored on TASK-1324
import { computed, ref, watch } from 'vue';
import { formatPhoneNumber } from '@Helpers';
import { AConfirmWordInput } from '@ShelterModule';

const emit = defineEmits<{
  onUsePerson: [result: any];
  onClose: [];
  page: [event: { page: number; first: number; rows: number }];
}>();

const props = withDefaults(
  defineProps<{
    hasConfirmationStep?: boolean;
    loading?: boolean;
    mode: string;
    paginationFirst?: number;
    paginationRows?: number;
    paginationTotalRecords?: number;
    results: any;
  }>(),
  {
    hasConfirmationStep: false,
    loading: false,
    mode: 'fill',
    paginationFirst: 0,
    paginationRows: 9,
  }
);

const CONFIRM_WORD = 'Proceed';

const results = ref(props.results);
const selectedPersonForConfirmation = ref<any>(null);
const confirmationInput = ref('');
const confirmWordInputRef = ref<InstanceType<typeof AConfirmWordInput> | null>(null);

const isConfirmValid = computed(() => confirmWordInputRef.value?.isValid ?? false);
const somePersonHasDoNotAdoptFlag = computed(() => results.value.some((result: any) => !!result.do_not_adopt));
const duplicatePersonCount = computed(() =>
  props.paginationTotalRecords != null ? props.paginationTotalRecords : results.value.length
);
const showPagination = computed(
  () => props.paginationTotalRecords != null && props.paginationTotalRecords > (props.paginationRows ?? 9)
);

watch(
  () => props.results,
  (val) => (results.value = val)
);

function handleClose() {
  results.value = [];
  selectedPersonForConfirmation.value = null;
  confirmationInput.value = '';

  emit('onClose');
}

function onPersonClick(result: any) {
  if (props.hasConfirmationStep && props.mode === 'fill' && !!result.do_not_adopt) {
    selectedPersonForConfirmation.value = result;
    confirmationInput.value = '';
    return;
  }

  emit('onUsePerson', result);
}

function onConfirmProceed() {
  if (!selectedPersonForConfirmation.value || !isConfirmValid.value) return;

  emit('onUsePerson', {
    ...selectedPersonForConfirmation.value,
    staff_confirmed_use_of_do_not_adopt_person: true,
  });
  selectedPersonForConfirmation.value = null;
  confirmationInput.value = '';
}

function onCancelConfirmation() {
  selectedPersonForConfirmation.value = null;
  confirmationInput.value = '';
}
</script>

<template>
  <div
    v-if="results.length || loading"
    class="align-center fixed left-0 top-0 z-[99999] flex h-screen w-screen flex-col items-center justify-center bg-black bg-opacity-40"
  >
    <div
      :class="[
        'm-4 flex max-h-[630px] w-full min-w-0 max-w-[640px] flex-col overflow-hidden rounded-xl bg-white p-4 shadow-lg',
        { 'bg-transparent': loading },
        selectedPersonForConfirmation ? 'h-fit' : 'h-full',
      ]"
    >
      <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <ALoadingState
          v-if="loading"
          scrollHeight="100%"
        />

        <template v-else>
          <Message
            v-if="results.length && !selectedPersonForConfirmation"
            id="person-help"
            class="mt-0 flex shrink-0 justify-center"
            :closable="false"
            :severity="mode === 'link' ? 'error' : 'warn'"
          >
            {{ $t('components.forms.errors.duplicateWarning') }}!
          </Message>

          <div
            v-else-if="selectedPersonForConfirmation"
            id="do-not-adopt-confirmation"
            class="mb-4 mt-8 flex flex-col items-center justify-center gap-2"
          >
            <i class="pi pi-exclamation-circle text-[70px] text-red-300" />

            <p class="text-center font-medium text-red-800">
              {{ $t('cart.doNotAdopt.confirmSelectDoNotAdopt') }}! <br />
              {{
                $t('cart.doNotAdopt.confirmSelect', {
                  userName: selectedPersonForConfirmation.full_name || $t('common.unknown'),
                })
              }}
            </p>
          </div>

          <div v-if="mode === 'link'">
            <div
              v-if="results.length"
              class="flex items-center justify-center gap-2 text-sm font-semibold text-red-600"
            >
              <p>
                {{
                  $t(
                    'components.forms.errors.duplicatedPerson',
                    { person: duplicatePersonCount },
                    duplicatePersonCount
                  ) +
                  '. ' +
                  $t('components.formFields.contact.selectToGo')
                }}.
              </p>

              <i class="pi pi-external-link text-sm"></i>
            </div>

            <div class="mt-4 grid min-h-0 grid-cols-2 items-start justify-center gap-1 p-0 sm:grid-cols-3 sm:gap-3">
              <RouterLink
                v-for="result in results"
                class="relative m-0 flex h-fit max-h-[110px] min-h-[110px] max-w-[223px] flex-row items-start justify-start gap-2 rounded-lg border border-solid p-2 text-center text-slate-600 transition-colors hover:bg-slate-100"
                :key="result.id"
                :to="`/shelter/people/profile/${result._id.$oid}`"
              >
                <div class="flex w-full flex-col gap-1 overflow-hidden text-ellipsis text-nowrap text-start">
                  <p class="flex items-center gap-1">
                    <span
                      v-if="result?.seq_id"
                      class="overflow-hidden text-ellipsis text-xs font-semibold"
                    >
                      {{ result.seq_id }} -
                    </span>

                    <span class="overflow-hidden text-ellipsis text-xs font-semibold">
                      {{ result.full_name || $t('common.unknown') }}
                    </span>
                  </p>

                  <div
                    v-if="result.addresses[0]?.address1"
                    class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]"
                  >
                    <AAddress
                      class="overflow-hidden text-ellipsis text-nowrap"
                      :address="result.addresses[0]"
                    />
                  </div>

                  <div class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]">
                    <span class="overflow-hidden text-ellipsis text-[10px]">
                      {{ formatPhoneNumber(result.phones[0]?.phone || '', result.phones[0]?.phone_national) }}
                    </span>
                  </div>

                  <div class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]">
                    <span class="overflow-hidden text-ellipsis text-[10px]">
                      {{ result.emails[0]?.email ? result.emails[0]?.email : '' }}
                    </span>
                  </div>
                </div>
              </RouterLink>
            </div>
          </div>

          <div v-else-if="mode === 'fill'">
            <div
              v-if="selectedPersonForConfirmation"
              class="flex flex-col items-center justify-center gap-4 pt-2"
            >
              <AConfirmWordInput
                v-model="confirmationInput"
                class="mx-auto max-w-[400px]"
                inputClass="w-full"
                messageKey="cart.doNotAdopt.typeToConfirm"
                ref="confirmWordInputRef"
                :confirmWord="CONFIRM_WORD"
                @enter="onConfirmProceed"
              />
            </div>

            <template v-else>
              <div
                v-if="results.length"
                class="flex items-center justify-center gap-2 text-sm font-semibold text-yellow-600"
              >
                <p>
                  {{
                    $t(
                      'components.forms.errors.duplicatedPerson',
                      { person: duplicatePersonCount },
                      duplicatePersonCount
                    ) +
                    '. ' +
                    $t('components.formFields.contact.selectToUse')
                  }}.
                </p>

                <i class="pi pi-user-plus text-sm"></i>
              </div>

              <div class="mt-4 grid min-h-0 grid-cols-2 items-start justify-center gap-1 p-0 sm:grid-cols-3 sm:gap-3">
                <div
                  v-for="result in results"
                  :key="result.id"
                  :class="[
                    'relative m-0 flex h-fit max-w-[223px] cursor-pointer flex-row items-start justify-start gap-2 rounded-lg border border-solid p-2 text-center text-slate-600 transition-colors',
                    hasConfirmationStep && !!result.do_not_adopt
                      ? 'border-red-600 hover:bg-red-50'
                      : 'hover:bg-slate-100',
                    hasConfirmationStep && somePersonHasDoNotAdoptFlag
                      ? 'max-h-[134px] min-h-[134px]'
                      : 'max-h-[110px] min-h-[110px]',
                  ]"
                  @click="onPersonClick(result)"
                >
                  <div class="flex w-full flex-col gap-1 overflow-hidden text-ellipsis text-nowrap text-start">
                    <p class="flex items-center gap-1">
                      <span
                        v-if="result?.seq_id"
                        class="overflow-hidden text-ellipsis text-xs font-semibold"
                      >
                        {{ result.seq_id }} -
                      </span>

                      <span class="overflow-hidden text-ellipsis text-xs font-semibold">
                        {{ result.full_name || $t('common.unknown') }}
                      </span>
                    </p>

                    <APersonFlags
                      compact
                      :doNotAdoptFlag="hasConfirmationStep && !!result.do_not_adopt"
                      :tags="result.tags || result.flags"
                    />

                    <div
                      v-if="result.addresses[0]?.address1"
                      class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]"
                    >
                      <AAddress
                        class="overflow-hidden text-ellipsis text-nowrap"
                        :address="result.addresses[0]"
                      />
                    </div>

                    <div class="flex items-center gap-1 text-[10px]">
                      <span class="overflow-hidden text-ellipsis text-[10px]">
                        {{ formatPhoneNumber(result.phones[0]?.phone || '', result.phones[0]?.phone_national) }}
                      </span>
                    </div>

                    <div class="flex items-center gap-1 text-[10px]">
                      <span class="overflow-hidden text-ellipsis text-[10px]">
                        {{ result.emails[0]?.email ? result.emails[0]?.email : '' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>

      <footer
        v-if="!selectedPersonForConfirmation"
        class="flex w-full shrink-0 flex-col gap-3 border-t border-slate-100 pt-4"
      >
        <div
          v-if="showPagination"
          class="flex min-h-[2.75rem] w-full items-center justify-center"
        >
          <Paginator
            pt:root="!flex-nowrap !items-center !justify-center !px-0 !pb-0"
            :first="paginationFirst ?? 0"
            :pageLinkSize="3"
            :rows="paginationRows ?? 9"
            :template="{
              '1024px': 'PrevPageLink CurrentPageReport NextPageLink',
              default: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink',
            }"
            :totalRecords="paginationTotalRecords ?? 0"
            @page="emit('page', $event)"
          />
        </div>

        <div class="flex w-full items-center justify-end">
          <Button
            class="af-button"
            severity="secondary"
            @click="handleClose"
          >
            {{ $t('common.cancel') }}
          </Button>
        </div>
      </footer>

      <footer
        v-else-if="selectedPersonForConfirmation"
        class="mt-4 flex shrink-0 justify-between gap-2 border-t border-slate-100 pt-4"
      >
        <Button
          class="af-button"
          severity="secondary"
          :label="$t('components.forms.button.goBack')"
          @click="onCancelConfirmation"
        />

        <Button
          class="af-button af-button-success"
          icon="pi pi-check"
          :disabled="!isConfirmValid"
          :label="CONFIRM_WORD"
          @click="onConfirmProceed"
        />
      </footer>
    </div>
  </div>
</template>
